"use client";

import type { FormEvent, ReactNode } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import {
  getKundaliTranslations,
  INTENT_BANNER_COPY,
  type KundaliLang,
  type KundaliLocale,
} from "./KundaliFormCard.i18n";
import { FormErrorMessage, KundaliSubmitButton } from "./KundaliFormCard.controls";
import type { SEOIntentConfig } from "@/lib/seo-intents";
import { seoCtaLabel, seoPromptLabel } from "./KundaliFormCard.cta";
import { reportPlanSummaryById } from "@/lib/report-plan-summary";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import type { FieldFunnel } from "./useFieldFunnel";
import { KundaliFormFields } from "./KundaliFormCard.field-groups";
import type { KundaliProgressiveCopy } from "./KundaliFormCard.progressive";
import type { KundaliField } from "@/lib/analytics-kundali";
import { useProgressiveKundaliFormState } from "./useProgressiveKundaliFormState";
import progressiveStyles from "./KundaliFormCard.progressive.module.css";
import { BELOW_STICKY_CHROME_SCROLL_MARGIN } from "@/lib/sticky-chrome-offsets";

export type CapturedSEOEntry = {
  config: SEOIntentConfig;
  sourcePath: string;
  /** The SPECIFIC paid report this visitor arrived to buy (the report landing
   * page's `next_report`), when the entry is a report-buying path. Lets the intent
   * banner restore the paid context that the generic intent config would lose —
   * so a seeker who tapped a ₹199 report CTA doesn't land on a generic free-entry screen
   * with no sign of what they came to buy (rules 40/43/46). */
  reportPlanId?: string;
  /** A collect-inputs bridge link (`open_checkout=1` + `origin_placement`) sent
   * this seeker: the result takes them straight to the plan's checkout once the
   * chart is ready (KundaliResult.helpers `primaryReportArrival`). */
  opensCheckout?: boolean;
};

type KundaliInputFormProps = {
  containerClassName: string;
  formClassName?: string;
  onSubmit: (event: FormEvent) => void;
  seoEntry: CapturedSEOEntry | null;
  language: KundaliLang;
  /** The language the fields are written in: a regional edition, else `language`. */
  locale?: KundaliLocale;
  setLanguage: (language: KundaliLang) => void;
  formReady: boolean;
  name: string;
  setName: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  selectedPlace: PlaceResult | null;
  onClearPlace: () => void;
  ambiguousSuggestion: PlaceResult | null;
  fieldFunnel: FieldFunnel;
  markFormStarted: (field: string) => void;
  fieldErrors: KundaliFieldErrors;
  error: string | null;
  loading: boolean;
  reassuranceNote?: string;
  deliveryField?: ReactNode;
  submitDisabled?: boolean;
  retryCard?: ReactNode;
  submitLabelOverride?: string;
  t: ReturnType<typeof getKundaliTranslations>;
  progressiveCopy?: KundaliProgressiveCopy;
  source: string;
  sourcePath?: string;
  initialFocusField?: KundaliField;
  showIntentBanner?: boolean;
  showLanguageToggle?: boolean;
};

export function KundaliInputForm({
  containerClassName,
  formClassName = "card-sacred space-y-5 p-6 sm:p-8",
  onSubmit,
  seoEntry,
  language,
  locale = language,
  setLanguage,
  formReady,
  name,
  setName,
  phone,
  setPhone,
  email,
  setEmail,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  selectedPlace,
  onClearPlace,
  ambiguousSuggestion,
  fieldFunnel,
  markFormStarted,
  fieldErrors,
  error,
  loading,
  reassuranceNote,
  deliveryField,
  submitDisabled,
  retryCard,
  submitLabelOverride,
  t,
  progressiveCopy,
  source,
  sourcePath,
  initialFocusField,
  showIntentBanner = true,
  showLanguageToggle = true,
}: KundaliInputFormProps) {
  const { visibleStep, showFinalAction, setProgressiveStep, trackProgressiveAction, submit } =
    useProgressiveKundaliFormState({
      progressiveCopy,
      initialFocusField,
      fieldErrors,
      language,
      source,
      sourcePath,
      phone,
      email,
      submitDisabled,
      onSubmit,
    });
  const formBusy = loading || !formReady;

  return (
    // The result replaces this container in place. finishKundali scrolls it to
    // the top of the viewport just before the swap commits, with the margin the
    // result anchors with, so the page lands once where the result will be (C12).
    <div
      className={`${containerClassName} ${BELOW_STICKY_CHROME_SCROLL_MARGIN}`}
      data-kundali-swap-anchor=""
    >
      <form
        translate="no"
        onSubmit={submit}
        noValidate
        className={formClassName}
        aria-busy={formBusy}
      >
        <fieldset
          disabled={formBusy}
          aria-busy={formBusy}
          className="m-0 min-w-0 border-0 p-0 space-y-5 disabled:opacity-80"
        >
          {showIntentBanner && <SEOIntentBanner seoEntry={seoEntry} language={language} />}
          {showLanguageToggle && <LanguageToggle language={language} setLanguage={setLanguage} />}
          <InputReassurance note={reassuranceNote} />
          <KundaliFormFields
            {...{
              language,
              locale,
              t,
              progressiveCopy,
              progressiveStep: visibleStep,
              setProgressiveStep,
              name,
              setName,
              phone,
              setPhone,
              email,
              setEmail,
              birthDate,
              setBirthDate,
              birthTime,
              setBirthTime,
              requireBirthTime,
              birthPlace,
              setBirthPlace,
              setSelectedPlace,
              selectedPlace,
              onClearPlace,
              ambiguousSuggestion,
              fieldFunnel,
              markFormStarted,
              fieldErrors,
              onProgressiveAction: trackProgressiveAction,
            }}
          />
          <FormErrorMessage error={error} />
          {retryCard}
          {showFinalAction && (
            <div className={progressiveCopy ? progressiveStyles.finalAction : undefined}>
              {deliveryField}
              <KundaliSubmitButton
                loading={loading}
                disabled={submitDisabled}
                label={submitLabelOverride || seoCtaLabel(seoEntry, language) || t.submit}
                loadingLabel={t.loading}
              />
              {!progressiveCopy && (
                <p className="text-[11px] text-center" style={{ color: "var(--ish-text-faint)" }}>
                  {t.savedNote}
                </p>
              )}
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}

function InputReassurance({ note }: { note?: string }) {
  if (!note) return null;
  return (
    <p className="rounded-md px-3 py-2 text-center text-caption" style={reassuranceStyle}>
      {note}
    </p>
  );
}

const reassuranceStyle = {
  background: "var(--ish-bg)",
  border: "1px solid var(--ish-border)",
  color: "var(--ish-text-muted)",
};

/** @internal */
export function SEOIntentBanner({
  seoEntry,
  language = "en",
}: {
  seoEntry: CapturedSEOEntry | null;
  language?: KundaliLang;
}) {
  if (!seoEntry) return null;
  const copy = INTENT_BANNER_COPY[language];
  const prompt = seoPromptLabel(seoEntry.config, language);
  const bannerStyle = {
    background: "var(--ish-accent-light)",
    border: "1px solid var(--ish-accent-border)",
    color: "var(--ish-text-muted)",
  } as const;
  const eyebrowStyle = {
    color: "var(--intent-brand-text)",
    letterSpacing: "0.12em",
  } as const;

  // Paid-report entry: the seeker tapped a specific report CTA (e.g. ₹199 Career
  // Report) and was routed here to build their chart first. Restore the buying
  // context the generic free-entry hero drops — name the report + its price +
  // say the report's payment step sits below the chart (rules 40/43/46).
  // Only a bridge arrival (open_checkout=1 + origin_placement) is taken straight
  // to the plan's checkout on the result, and only with a birth time; any other
  // report arrival finds the pay step below the chart, so only the bridge copy may
  // promise the checkout.
  // Report names stay in the catalog's English form: this banner is Hinglish copy.
  const report = seoEntry.reportPlanId ? reportPlanSummaryById(seoEntry.reportPlanId) : null;
  if (report) {
    return (
      <div className="rounded-lg p-3.5 text-body-small" style={bannerStyle}>
        <p
          className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
          style={eyebrowStyle}
        >
          <span aria-hidden>ॐ</span> {copy.unlocking(report.name, report.priceInr)}
        </p>
        <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
          {prompt}
        </p>
        <p className="mt-1 text-caption">
          {seoEntry.opensCheckout
            ? copy.bridgeReportBody(report.name, report.priceInr)
            : copy.reportBody(report.name, report.priceInr)}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-3.5 text-body-small" style={bannerStyle}>
      <p
        className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={eyebrowStyle}
      >
        <span aria-hidden>ॐ</span> {copy.questionEyebrow}
      </p>
      <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
        {prompt}
      </p>
      <p className="mt-1 text-caption">{copy.questionBody}</p>
    </div>
  );
}

function LanguageToggle({
  language,
  setLanguage,
}: {
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
}) {
  return (
    <div className="flex justify-center">
      <div
        role="tablist"
        aria-label="Language"
        className="flex p-1 rounded-full"
        style={{ background: "var(--ish-bg)", border: "1px solid var(--ish-border)" }}
      >
        {(["en", "hi"] as KundaliLang[]).map((lang) => (
          <button
            key={lang}
            type="button"
            role="tab"
            // Each label is in its own language ("English" | "हिंदी"), so a
            // screen reader voices the other script correctly (c-a11y-11).
            lang={lang}
            aria-selected={language === lang}
            onClick={() => setLanguage(lang)}
            className="px-4 py-1.5 rounded-full text-caption font-semibold transition-all duration-200"
            style={{
              color: language === lang ? "#fff" : "var(--ish-text-muted)",
              backgroundColor: language === lang ? "var(--ish-accent)" : "transparent",
            }}
          >
            {lang === "en" ? "English" : "हिंदी"}
          </button>
        ))}
      </div>
    </div>
  );
}
